import { getAssetPath, VITE_ENVIRONMENT } from '@/constants/configs';

import { QuestionCode } from '@/types/api/pep';
import {
  AppConfigurations,
  ExtraBasicInfoQuestions,
} from '@/types/app-configurations';
import { Environment } from '@/types/common';
import { DocumentType, SigningFormType } from '@/types/document';
import { Language } from '@/types/i18n';
import { ResidenceType, User } from '@/types/user';

import { isMinorUser } from '@/utils/user';
import { isAboveYearsOld } from '@/utils/validation';

export const resolveICBWebUrl = (
  productionUrl: string,
  environment: string | undefined = VITE_ENVIRONMENT,
): string => {
  switch (environment) {
    case Environment.Development:
      return 'https://republic-dev.web.app';
    case Environment.Test:
      return 'https://republic-tst.web.app';
    case Environment.Staging:
      return 'https://republic-stg.web.app';
    default:
      return productionUrl;
  }
};

export const defaultConfigs: AppConfigurations = {
  links: {
    privacyPolicyText: 'common.countryPrivacyPolicy',
    privacyPolicyURL: '',
    termsAndConditionsText: 'common.countryTermsAndConditions',
    termsAndConditionsURL: '',
    termsOfUseText: 'common.roTermOfUse',
    termsOfUseURL: '',
    appTermsAndConditionsText: 'common.termsAndConditions',
    appTermsAndConditionsURL: '',
    biometricConsentText: 'common.biometricConsent',
    biometricConsentURL: '',
    icbWeb: '',
    icbIOS: '',
    icbAndroid: '',
    savingsPlusURL: 'https://republictt.com/personal/savings-plus',
    picaURL:
      'https://republictt.com/personal/personal-interest-chequing-account',
  },
  logo: getAssetPath('/logo-with-text.svg'),
  brandingLogoLight: getAssetPath('/logo-with-text-white.svg'),
  brandingImageInfoPage: getAssetPath('/bg-register.png'),
  brandingImageAddressPage: '',
  defaultCountryCode: '',
  defaultCurrency: 'USD',
  currencyPlacement: {
    [Language.English]: 'after',
    [Language.Dutch]: 'after',
  },
  locale: '',
  isoCode: '',
  dateFormat: 'dd/MM/yyyy',
  proofOfEmploymentDescriptionByType: {
    [DocumentType.LetterOfEmployment]: {
      title: `proofOfEmploymentDescriptionByType.${DocumentType.LetterOfEmployment}`,
      description: 'documentTypeSubtitle.ofLessThan3MonthsParens',
    },
  },
  accountTypeMaxAmount: -1,
  scanIDAutoCaptureSeconds: 20,
  idDocumentTypes: [DocumentType.Id, DocumentType.Passport],
  idCaptureRequirement: 'idCardOrPassport',
  idTypesWithoutExpiryDate: [DocumentType.Nis, DocumentType.BirthCertificate],
  idDefaultExpiryDate: '2079-06-06',
  requiredLocalTIN: false,
  formsOnEachAccountType: [SigningFormType.Authorization],
  autoCompleteAdultWithNoDocumentSigning: false,
  availableLanguages: [Language.English],
  jointAccountRelationshipOptions: [
    { value: 'PARENT', i18nKey: 'minorOthers.form.relationship.parent' },
    {
      value: 'STEP_PARENT',
      i18nKey: 'minorOthers.form.relationship.stepParent',
    },
    {
      value: 'ADOPTIVE_PARENT',
      i18nKey: 'minorOthers.form.relationship.adoptiveParent',
    },
    {
      value: 'LEGAL_GUARDIAN',
      i18nKey: 'minorOthers.form.relationship.legalGuardian',
    },
    {
      value: 'FOSTER_PARENT',
      i18nKey: 'minorOthers.form.relationship.fosterParent',
    },
    {
      value: 'GRANDPARENT',
      i18nKey: 'minorOthers.form.relationship.grandparent',
    },
    {
      value: 'AUNT_UNCLE',
      i18nKey: 'minorOthers.form.relationship.auntUncle',
    },
    {
      value: 'OLDER_SIBLING',
      i18nKey: 'minorOthers.form.relationship.olderSibling',
    },
  ],
  residenceOptions: [
    {
      value: ResidenceType.Own,
      i18nKey: `createNewAccount.addressPage.residenceType.${ResidenceType.Own}`,
    },
    {
      value: ResidenceType.Rent,
      i18nKey: `createNewAccount.addressPage.residenceType.${ResidenceType.Rent}`,
    },
    {
      value: ResidenceType.Company,
      i18nKey: `createNewAccount.addressPage.residenceType.${ResidenceType.Company}`,
    },
    {
      value: ResidenceType.Family,
      i18nKey: `createNewAccount.addressPage.residenceType.${ResidenceType.Family}`,
    },
    {
      value: ResidenceType.Other,
      i18nKey: `createNewAccount.addressPage.residenceType.${ResidenceType.Other}`,
    },
  ],
  shouldShowMaritalStatus: false,
  shouldShowGDPRDrawer: false,
  excludedBranchCodes: [],
  maxTaxResidencySelection: 3,
  // TODO: EXC - remove when rolling out to all tenants
  isExcEnabled: true,
  showRiskSectorFlagQuestion: true,
  showSelfEmployedStartupQuestion: true,
  showSelfEmployedWithRiskSectorFlagQuestion: false,
  shouldRequireMaritalStatus: true,
  shouldAddSecondPhoneNumber: true,
  shouldShowUsername: true,
  shouldShowIssuance: true,
  shouldShowCityIssuance: false,
  shouldShowCityOfBirth: false,
  shouldShowMotherMaidenName: true,
  shouldShowEmailNotificationPopup: false,
  shouldShowLeftBrandingLogo: false,
  shouldShowRightBrandingLogo: true,
  shouldRejectNonResidentMinor: false,
  existingCustomer: {
    maxAccountsPerType: -1,
  },
  extraPhoneOptions: [{ value: 'Other', text: 'Others' }],
  canAbandonApplication: true,
  isProofOfRelationshipBlockedByBasicInfo: true,
  isMultipleMonthlyDeposits: false,
  minorOnlyIdDocumentTypes: [DocumentType.BirthCertificate],
  overridePhoneNumberSchema: {
    Other: {
      maxLength: 14,
      pattern: '^\\d{6,14}$',
    },
  },
  childLivesWithRelationshipOptions: [],
};

export const getExtraBasicInfoQuestions = (
  user: User,
): ExtraBasicInfoQuestions => {
  if (isMinorUser(user)) {
    return {
      ...getMinorExtraBasicInfoQuestions(user),
    };
  }
  return {
    [QuestionCode.USIndica]: {
      enabled: user.usIndicia === undefined,
      question: 'questionDrawer.USIndicia.question',
      priority: 1,
    },
    [QuestionCode.DualCitizenship]: {
      enabled: user.citizenship2Has === undefined,
      question: `question.${QuestionCode.DualCitizenship}`,
      priority: 2,
    },
    [QuestionCode.ExpectedMonthlyDeposit]: {
      enabled: user.monthlyDeposit === undefined,
      question: 'questionDrawer.expectedMonthlyDeposit',
      priority: 3,
    },
    [QuestionCode.FundingSource]: {
      enabled: user.fundingSource === undefined,
      question: 'questionDrawer.sourceOfFunding',
      priority: 4,
    },
  };
};

const getMinorExtraBasicInfoQuestions = (
  minor: User,
): ExtraBasicInfoQuestions => {
  return {
    [QuestionCode.USIndica]: {
      enabled: minor.usIndicia === undefined,
      question: 'questionDrawer.USIndicia.minor.question',
      priority: 0,
    },
    [QuestionCode.DualCitizenship]: {
      enabled: minor.citizenship2Has === undefined,
      question: `question.${QuestionCode.DualCitizenship}`,
      priority: 1,
    },
    [QuestionCode.MinorOther]: {
      enabled: minor?.hasPhoneNumber === undefined,
      question: 'questionDrawer.minorOther',
      priority: 2,
    },
    [QuestionCode.MinorConsentSign]: {
      enabled:
        isAboveYearsOld(13, minor?.birthday) &&
        minor?.hasPhoneNumber === true &&
        minor?.allowToDebitCardOnlineBanking === undefined,
      question: 'questionDrawer.minorConsentSign',
      priority: 3,
    },
  };
};
